import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, pollJob, jobErrorMessage, newIdemKey, idemHeaders } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Loader2, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { CONTENT_PLATFORMS, slug } from "./constants";

const DEFAULTS = {
  goal_text: "Enroll 15 people in the Clinical Trichology Intensive",
  goal_number: 15,
  length_days: 30,
  start_date: "",
  platforms: ["Instagram", "TikTok", "Facebook", "YouTube"],
  approval_preference: "Weekly Batch Review",
};

export default function LaunchCampaignDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState({ business_id: "", offer_id: "", ...DEFAULTS });
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/businesses"), api.get("/marketing/offers")]).then(([b, o]) => {
      setBusinesses(b.data);
      setOffers(o.data.filter((x) => x.status === "Active"));
      const uni = b.data.find((x) => x.name.includes("KISSED University"));
      const intensive = o.data.find((x) => x.name.includes("Intensive"));
      setForm((f) => ({ ...f, business_id: uni?.id || b.data[0]?.id || "", offer_id: intensive?.id || o.data[0]?.id || "" }));
      setStrategy(null);
    });
  }, [open]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const togglePlatform = (p) => setForm((f) => ({ ...f, platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p] }));

  const plan = async () => {
    if (!form.offer_id) { toast.error("Select an offer"); return; }
    if (!form.goal_text.trim()) { toast.error("Enter a goal"); return; }
    setLoading(true);
    setProgress("Designing your campaign strategy…");
    try {
      const body = {
        business_id: form.business_id, offer_id: form.offer_id, goal_text: form.goal_text,
        goal_number: form.goal_number ? Number(form.goal_number) : null, length_days: Number(form.length_days),
        start_date: form.start_date || null, platforms: form.platforms, approval_preference: form.approval_preference,
      };
      const { data } = await api.post("/marketing/ai/campaign/plan", body, idemHeaders(newIdemKey()));
      const job = await pollJob(data.job_id, { onUpdate: (j) => setProgress(`Strategy ${j.status.toLowerCase()}…`) });
      if (job.status === "Failed" || job.status === "Cancelled") { toast.error(jobErrorMessage(job)); return; }
      setStrategy(job.result?.strategy);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Strategy generation failed"); }
    finally { setLoading(false); setProgress(""); }
  };

  const activate = async () => {
    setLoading(true);
    setProgress("Activating campaign and generating the first 7-day batch…");
    try {
      const { data } = await api.post("/marketing/ai/campaign/activate", {
        business_id: form.business_id, offer_id: form.offer_id, goal_text: form.goal_text,
        goal_number: form.goal_number ? Number(form.goal_number) : null, length_days: Number(form.length_days),
        start_date: form.start_date || null, platforms: form.platforms, approval_preference: form.approval_preference,
        strategy,
      }, idemHeaders(newIdemKey()));
      toast.success("Campaign activated — first batch generating in the background");
      onOpenChange(false);
      navigate(`/marketing/campaigns/${data.campaign.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Activation failed"); }
    finally { setLoading(false); setProgress(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="launch-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Wand2 className="h-5 w-5 text-[#581C87]" /> Launch AI Campaign</DialogTitle>
          <DialogDescription>Enter a plain-language goal — the AI Campaign Director designs the full strategy and first content batch.</DialogDescription>
        </DialogHeader>

        {!strategy ? (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Business</Label>
                <Select value={form.business_id} onValueChange={(v) => upd("business_id", v)}>
                  <SelectTrigger data-testid="launch-business-select" className="focus:ring-[#581C87]"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Offer</Label>
                <Select value={form.offer_id} onValueChange={(v) => upd("offer_id", v)}>
                  <SelectTrigger data-testid="launch-offer-select" className="focus:ring-[#581C87]"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Plain-language goal</Label>
              <Textarea value={form.goal_text} onChange={(e) => upd("goal_text", e.target.value)} data-testid="launch-goal-input" className="focus-visible:ring-[#581C87]" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Goal number (optional)</Label><Input type="number" value={form.goal_number} onChange={(e) => upd("goal_number", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
              <div className="space-y-2">
                <Label>Campaign length</Label>
                <Select value={String(form.length_days)} onValueChange={(v) => upd("length_days", Number(v))}>
                  <SelectTrigger data-testid="launch-length-select" className="focus:ring-[#581C87]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="7">7 days</SelectItem><SelectItem value="14">14 days</SelectItem><SelectItem value="30">30 days</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Start date (optional)</Label><Input type="date" value={form.start_date} onChange={(e) => upd("start_date", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            </div>
            <div className="space-y-2">
              <Label>Platforms</Label>
              <div className="flex flex-wrap gap-2">
                {CONTENT_PLATFORMS.map((p) => (
                  <button key={p} type="button" onClick={() => togglePlatform(p)} data-testid={`launch-platform-${slug(p)}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${form.platforms.includes(p) ? "bg-[#581C87] text-white border-[#581C87]" : "bg-white text-gray-600 border-gray-200 hover:border-[#581C87]"}`}>{p}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Approval preference</Label>
              <Select value={form.approval_preference} onValueChange={(v) => upd("approval_preference", v)}>
                <SelectTrigger data-testid="launch-approval-select" className="focus:ring-[#581C87]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Review Every Item">Review every item</SelectItem>
                  <SelectItem value="Weekly Batch Review">Review one weekly batch</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2" data-testid="strategy-preview">
            <h3 className="font-heading font-bold text-lg">{strategy.campaign_name}</h3>
            <p className="text-sm text-gray-600">{strategy.positioning}</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-xs uppercase tracking-widest text-gray-400">Key Message</span><p className="text-gray-800">{strategy.key_message}</p></div>
              <div><span className="text-xs uppercase tracking-widest text-gray-400">Ethical Promise</span><p className="text-gray-800">{strategy.ethical_promise}</p></div>
              <div><span className="text-xs uppercase tracking-widest text-gray-400">Pillars</span><p className="text-gray-800">{(strategy.content_pillars || []).join(", ")}</p></div>
              <div><span className="text-xs uppercase tracking-widest text-gray-400">Platform Mix</span><p className="text-gray-800">{(strategy.platform_mix || []).join(", ")}</p></div>
              <div><span className="text-xs uppercase tracking-widest text-gray-400">CTA Keyword</span><p className="text-gray-800">{strategy.cta_keyword}</p></div>
              <div><span className="text-xs uppercase tracking-widest text-gray-400">Cadence</span><p className="text-gray-800">{strategy.recommended_cadence}</p></div>
            </div>
            <div><span className="text-xs uppercase tracking-widest text-gray-400">Content Ratio</span><p className="text-gray-800 text-sm">{strategy.content_ratio ? Object.entries(strategy.content_ratio).map(([k, v]) => `${k}: ${v}`).join("   ") : "—"}</p></div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {progress && (
            <p className="w-full text-xs text-[#581C87] flex items-center gap-2" data-testid="launch-progress">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {progress}
            </p>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="launch-cancel-button">Cancel</Button>
          {!strategy ? (
            <Button onClick={plan} disabled={loading} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="generate-strategy-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-2" /> Generate Strategy</>}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStrategy(null)} disabled={loading} data-testid="strategy-edit-button">Edit</Button>
              <Button variant="outline" onClick={plan} disabled={loading} data-testid="strategy-regenerate-button">Regenerate</Button>
              <Button onClick={activate} disabled={loading} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="strategy-activate-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activate & Generate"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
